import { gql } from 'graphql-request';

import {
  GroupedItemBatchLabel,
  GroupedItemConvertedPrice,
  GroupedItemConvertedPricePerItem,
  GroupedItemInvoiceStatus,
  GroupedItemItemCategoryName,
  GroupedItemItemCreatedAt,
  GroupedItemItemID,
  GroupedItemItemName,
  GroupedItemMetaConvertedTotalPrice,
  GroupedItemMetaConvertedTotalPriceCents,
  GroupedItemMetaTotalPrice,
  GroupedItemMetaTotalPriceCents,
  GroupedItemProjectClosureMonth,
  GroupedItemProjectCreatedAt,
  GroupedItemProjectName,
  GroupedItemProjectNanoID,
  GroupedItemQuantity,
  GroupedItemSplitPartNumber,
  GroupedItemSplitPartPercent,
  GroupedItemTaskCreatedAt,
  GroupedItemTaskDueDateAt,
  GroupedItemTaskName,
  GroupedItemTaskNanoID,
  GroupedItemTaskStatus
} from '../groupedItemsTypes';
import {
  GroupedItemSmartContractTaskProjectId,
  GroupedItemSmartContractTaskProjectName,
  InvoiceClientId,
  InvoiceCreatedAt,
  InvoiceID,
  InvoiceInvoiceType,
  InvoiceName,
  InvoiceNanoID,
  InvoiceStatus,
  InvoiceTag
} from '../../invoices/invoicesTypes';
import { SmartContractStatus } from '../../smartContracts/smartContractsTypes';
import {
  ProjectOwnerClient,
  ProjectOwnerCurrentTeamNanoID,
  ProjectOwnerFullName,
  ProjectOwnerNanoID
} from '../../projects/projectsTypes';
import {
  TaskOwnerClient,
  TaskOwnerCurrentTeamNanoID,
  TaskOwnerFullName,
  TaskOwnerNanoID
} from '../../tasks/tasksTypes';
import { MayBe } from '../../../types';

export interface FetchedCacheGroupedInvoiceItemsQueryResponse {
  batchLabel: GroupedItemBatchLabel;
  convertedPrice: GroupedItemConvertedPrice;
  convertedPricePerItem: GroupedItemConvertedPricePerItem;
  itemCategoryName: GroupedItemItemCategoryName;
  itemCreatedAt: GroupedItemItemCreatedAt;
  itemId: GroupedItemItemID;
  itemTypeName: GroupedItemItemName;
  items: {
    id: GroupedItemItemID;
    convertedPrice: GroupedItemConvertedPrice;
    invoices: {
      id: InvoiceID;
      status: InvoiceStatus;
      invoiceType: InvoiceInvoiceType;
      invoiceTag: InvoiceTag;
    }[];
    smartContract: {
      status: SmartContractStatus;
      task: {
        project: {
          id: GroupedItemSmartContractTaskProjectId;
          name: GroupedItemSmartContractTaskProjectName;
        };
      };
    };
    tempInvoices: {
      id: InvoiceID;
      nanoId: InvoiceNanoID;
      clientId: InvoiceClientId;
      name: InvoiceName;
      invoiceType: InvoiceInvoiceType;
      status: InvoiceStatus;
      invoiceTag: InvoiceTag;
    }[];
  }[];
  invoiceStatus: GroupedItemInvoiceStatus;
  invoiceCreationMonth: InvoiceCreatedAt;
  projectCreatedAt: GroupedItemProjectCreatedAt;
  projectName: GroupedItemProjectName;
  projectNanoId: GroupedItemProjectNanoID;
  projectClosureMonth: GroupedItemProjectClosureMonth;
  projectOwner?: {
    nanoId: ProjectOwnerNanoID;
    fullName: ProjectOwnerFullName;
    client?: ProjectOwnerClient;
    currentCompany?: MayBe<{
      nanoId: ProjectOwnerCurrentTeamNanoID;
    }>;
  };
  rootProjectCreatedAt: GroupedItemProjectCreatedAt;
  rootProjectName: GroupedItemProjectName;
  rootProjectNanoId: GroupedItemProjectNanoID;
  rootProjectClosureMonth: GroupedItemProjectClosureMonth;
  rootProjectOwner?: {
    nanoId: ProjectOwnerNanoID;
    fullName: ProjectOwnerFullName;
    client?: ProjectOwnerClient;
    currentCompany?: MayBe<{
      nanoId: ProjectOwnerCurrentTeamNanoID;
    }>;
  };
  splitPartNumber: GroupedItemSplitPartNumber;
  splitPartPercent: GroupedItemSplitPartPercent;
  taskCreatedAt: GroupedItemTaskCreatedAt;
  taskDueDateAt: GroupedItemTaskDueDateAt;
  taskDueDateMonth: GroupedItemTaskCreatedAt;
  taskDoneMonth: GroupedItemTaskCreatedAt;
  taskName: GroupedItemTaskName;
  taskNanoId: GroupedItemTaskNanoID;
  taskOwner?: {
    nanoId: TaskOwnerNanoID;
    fullName: TaskOwnerFullName;
    client?: TaskOwnerClient;
    currentCompany?: MayBe<{
      nanoId: TaskOwnerCurrentTeamNanoID;
    }>;
  };
  taskStatus: GroupedItemTaskStatus;
  quantity: GroupedItemQuantity;
}

export interface FetchedCacheGroupedInvoiceItemsQueryResponseMeta {
  totalPrice: GroupedItemMetaTotalPrice;
  convertedTotalPrice: GroupedItemMetaConvertedTotalPrice;
  totalPriceCents: GroupedItemMetaTotalPriceCents;
  convertedTotalPriceCents: GroupedItemMetaConvertedTotalPriceCents;
}

export const CACHE_FETCH_GROUPED_INVOICE_ITEMS_QUERY = gql`
  query CacheGroupedItemsForInvoice(
    $convertTo: Currency
    $currencyRateId: ID
    $filters: GroupedItemsFilters
    $groupItemsByType: Boolean
    $groupBy: [ItemGroupBy]
    $limit: Int
    $sort: [GroupedItemsSortEnum!]
    $offset: Int
    $source: String
    $trackTotalHits: Boolean
    $withMetadata: Boolean
  ) {
    groupedItems(
      convertTo: $convertTo
      currencyRateId: $currencyRateId
      filters: $filters
      groupItemsByType: $groupItemsByType
      groupBy: $groupBy
      sort: $sort
      limit: $limit
      offset: $offset
      source: $source
      trackTotalHits: $trackTotalHits
      withMetadata: $withMetadata
    ) {
      nodes {
        convertedPrice
        convertedPricePerItem
        itemCategoryName
        itemCreatedAt
        itemId
        itemTypeName
        items {
          id
          convertedPrice
          smartContract {
            status
            task {
              project {
                id
                name
              }
            }
          }
          invoices {
            id
            invoiceType
            status
            invoiceTag
          }
          tempInvoices {
            id
            nanoId
            clientId
            name
            invoiceType
            status
            invoiceTag
          }
        }
        invoiceStatus
        invoiceCreationMonth
        projectCreatedAt
        projectName
        projectNanoId
        projectClosureMonth
        projectOwner {
          nanoId
          fullName
          client
          currentCompany {
            nanoId
          }
        }
        rootProjectCreatedAt
        rootProjectName
        rootProjectNanoId
        rootProjectClosureMonth
        rootProjectOwner {
          nanoId
          fullName
          client
          currentCompany {
            nanoId
          }
        }
        splitLabel
        splitPartNumber
        splitPartPercent
        taskCreatedAt
        taskDueDateAt
        taskDueDateMonth
        taskDoneMonth
        taskName
        taskNanoId
        taskStatus
        taskOwner {
          nanoId
          fullName
          client
          currentCompany {
            nanoId
          }
        }
        quantity
      }
      meta {
        totalPrice
        convertedTotalPrice
        totalPriceCents
        convertedTotalPriceCents
      }
      paginationInfo {
        currentPage
        nextPage
        totalCount
      }
      sql
    }
  }
`;
